import {SelectionModel} from "@angular/cdk/collections";
import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, signal} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {DateRange} from "@angular/material/datepicker";
import {MatDialog} from "@angular/material/dialog";
import {TabManagementService} from "@core-services/tab-management.service";
import {PageEventInterface} from "@core/interfaces/page-event.interface";
import {PageOffering} from "@core/model/side-nav.model";
import {CrudLease} from "@features/leasing/crud-lease.model";
import {RetailerLookupComponentOptions} from "@shared-components/retailer-lookup/retailer-lookup.model";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {UtilsService} from "@shared-services/utils.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {CustomDateRangeModel, TimeFrames} from "@ui-kit/webkit/components/custom-date-filter/custom-date-range.model";
import {ApplicationAssessmentStatus, Retailer, TenantType} from "../../../leads/retailers/retailers.model";
import {
  CreateASubLeaseDialogComponent
} from "../../create-a-lease/create-a-sub-lease-dialog/create-a-sub-lease-dialog.component";
import {AgreementType, LeaseStatus, LeaseSummary} from "../../leasing.model";
import {LeaseViewComponent} from "../lease-view.component";
import {LeaseViewService} from "../lease-view.service";
import {LeaseSubLeasesService} from "./lease-sub-leases.service";

@Component({
  selector: 'app-lease-sub-leases',
  templateUrl: './lease-sub-leases.component.html',
})
export class LeaseSubLeasesComponent implements OnInit, AfterViewInit {
  @Input() id!: string;
  @Input() offering!: PageOffering;
  @Output() reloadLease = new EventEmitter<void>();

  retailerLookupOptions = signal<Partial<RetailerLookupComponentOptions>>({
    allowedRetailerTenantTypes : [TenantType.TENANT],
    allowedCurationStatus: [ApplicationAssessmentStatus.APPROVED]
  });

  currentPage = 0;
  pageSize = 1000;

  parentLease!: LeaseSummary;

  displayedColumns: string[] = ['uuid', 'retailerCompanyName', 'leaseStartDate', 'leaseEndDate', 'spaceSize', 'rentalType', 'monthlyFee', 'expiresIn', 'leaseStatus', 'tradingOutsideOfLeasePeriod'];

  baseLeaseData: CrudLease[] = [];
  filteredLeaseData: CrudLease[] = [];
  selection = new SelectionModel<Retailer>(true, []);

  leaseStatuses = [LeaseStatus.INVALID, LeaseStatus.PLANNED, LeaseStatus.APPROVED, LeaseStatus.ACTIVE, LeaseStatus.EXPIRED];

  filterForm: FormGroup;
  dateRange: DateRange<Date | null> = new DateRange<Date | null>(null, null);
  dateFilters: CustomDateRangeModel[] = [
    new CustomDateRangeModel('1', 2, TimeFrames.DAYS),
    new CustomDateRangeModel('2', 1, TimeFrames.WEEK),
    new CustomDateRangeModel('3', 1, TimeFrames.MONTH),
  ]

  createSubLeaseOpen: boolean = false;

  constructor(public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private tabService: TabManagementService,
              private leaseViewService: LeaseViewService,
              private subLeaseService: LeaseSubLeasesService,
              private matDialog: MatDialog,
              public loader: LoadingService) {
    this.filterForm = new FormGroup({
      leaseStatus: new FormControl([]),
      tradingOutsideOfLeasePeriod: new FormControl([]),
      expiresIn: new FormControl([]),
    })
  }

  ngOnInit() {
    this.loadParentLease();
  }

  ngAfterViewInit() {
    this.filterForm.valueChanges.subscribe(() => {
      this.filterLeaseData();
    });
  }

  loadParentLease() {
    this.leaseViewService.getOne('', this.id).subscribe({
      next: value => {
        this.parentLease = value;
        this.loadInitialLeaseData();
      },
      error: err => {
        console.log(err);
      }
    })
  }

  loadInitialLeaseData() {
    this.currentPage = 0;
    this.baseLeaseData = [];
    this.loadLeases();
  }

  loadMoreLeases() {
    this.currentPage++;
    this.loadLeases();
  }

  loadLeases() {
    let filters = [
      // { name: 'statusList', val: this.filterForm.get('leaseStatus')?.value.length === 0 ? this.leaseStatuses : this.filterForm.get('leaseStatus')?.value},
      {name: 'leaseStartDate', val: this.dateUtils.displayShortDate(this.dateRange.start)},
      {name: 'leaseEndDate', val: this.dateUtils.displayShortDate(this.dateRange.end)},
      {name: 'parentUuid', val: this.parentLease.uuid},
      {name: 'agreementType', val: AgreementType.SUB_LEASE}
    ];
    filters = filters.filter(item => item.val !== null && item.val !== undefined);
    const page = {
      size: this.pageSize.toString(),
      page: this.currentPage.toString(),
      sort: 'leaseStartDate,desc'
    };

    const allData = [...this.baseLeaseData];
    this.subLeaseService.getAll('', page, filters).subscribe({
      next: value => {
        const newData = value.content.map(m => new CrudLease(m));
        allData.push(...newData);
        this.baseLeaseData = [...allData];
        this.filterLeaseData();
      },
      error: err => {
        console.log(err);
      }
    })
  }

  filterLeaseData() {
    this.filteredLeaseData = this.baseLeaseData.filter(lease => {
      const statusFilter = this.filterForm.get('leaseStatus')?.value;
      const tradingStatusFilter = this.filterForm.get('tradingOutsideOfLeasePeriod')?.value;
      const expiresInFilter = this.filterForm.get('expiresIn')?.value;

      const statusIncluded = this.leaseStatuses && this.leaseStatuses.includes(lease.leaseStatus);
      const statusValid: boolean = statusFilter && statusFilter.length == 0 || statusFilter.includes(lease.leaseStatus);
      const tradingStatusValid: boolean = tradingStatusFilter && tradingStatusFilter.length == 0 || tradingStatusFilter.includes(lease.tradingOutsideOfLeasePeriod);
      const expiresInValid: boolean = expiresInFilter && expiresInFilter.length == 0 || (lease.expiresInDays != null && Math.max(...expiresInFilter) >= lease.expiresInDays);

      return statusIncluded && statusValid && tradingStatusValid && expiresInValid;
    })
  }

  openLeaseViewTab(sublease: LeaseSummary) {
    let payload: PageEventInterface = {
      componentToRegister: LeaseViewComponent,
      pageName: 'Lease ' + this.utils.displayUuid(sublease.uuid),
      pageHeader: this.offering!.label,
      data: {
        id: sublease.uuid,
        occupancyType: sublease.occupancyType,
        agreementType: sublease.agreementType
      },
      id: this.utils.generateUuidWithPrefix('lease'),
      offeringId: this.offering!.offeringUuid,
      sectionCode: 'leasing',
      pageCode: 'lease-schedule',
      offering: this.offering
    }
    this.tabService.sendPageAddEvent(payload);
  }

  createSubLease() {
    this.createSubLeaseOpen = true;
  }

  reloadSubLeaseData(reload: boolean) {
    if (reload) {
      this.createSubLeaseOpen = false;
      this.loadLeases();
      this.reloadLease.emit();
    }
  }

  protected readonly CrudLease = CrudLease;
}
